import * as R from 'ramda'
import moment from 'moment'
import { ALL_FILTERS_KEYS, DEPARTURE_CITIES_NO_TRANSPORTATION_CODES } from 'constants/filters'
import { CityWithKey } from 'types/typescript'
import {
  AreValuesDifferent,
  GetCitiesInfoFromCode,
  GetDateByOffsetAndSaleBeginDate,
  GetDepartureCitiesFromTransportation,
  GetDepartureDatesFromOffset,
  GetFiltersFromQuery,
  GetFiltersQueryIntoValues,
  GetFormattedCurrentDay,
  GetOffsetsWithFormattedDateList,
  GetPathnameString,
  GetRefreshedFilters,
  GetSearchString,
  GetTransportationFromDepartureCities,
  IsValidList,
  PathnameHasChanged,
  PathnameHasNotChanged
} from './filters.types'
import { sortDepartureCityOptions } from './pricerDepartureCity'

const getPathnameString: GetPathnameString = R.path(['payload', 'location', 'pathname'])

const getSearchString: GetSearchString = R.path(['payload', 'location', 'search'])

export const areValuesDifferent: AreValuesDifferent = values => !R.equals(...values)

export const getFiltersQueryIntoValues: GetFiltersQueryIntoValues = R.pipe(
  R.defaultTo(''),
  R.split('&'),
  R.map(R.split('=')),
  R.fromPairs,
  R.pick(ALL_FILTERS_KEYS)
)

export const getFiltersFromQuery: GetFiltersFromQuery = R.pipe(getSearchString, getFiltersQueryIntoValues)

export const pathnameHasChanged: PathnameHasChanged = R.pipe(
  R.map(getPathnameString),
  areValuesDifferent
)

export const pathnameHasNotChanged: PathnameHasNotChanged = R.pipe(
  pathnameHasChanged,
  R.not
)

export const getOffsetsWithFormattedDateList: GetOffsetsWithFormattedDateList = (departureDates, beginDate) => {
  const currentDay = moment()

  return (
    R.reduce((acc, offset) => {
      const date = moment(beginDate).add(offset, 'days').format('YYYY-MM-DD')

      if (currentDay.isBefore(date)) {
        acc.push({ date, offset })
      }
      return acc
    }, [])(departureDates)
  )
}

export const getCitiesInfoFromCode: GetCitiesInfoFromCode = (favoriteDepartureCity, resourceCities, cities) => {
  const citiesWithLabels = (
    cities.reduce((acc: CityWithKey[], value) => {
      if (resourceCities.includes(value.key) && !DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(value.key)) {
        acc.push(value)
      }
      return acc
    }, [])
  )

  return sortDepartureCityOptions(favoriteDepartureCity, citiesWithLabels)
}

export const getDepartureDatesFromOffset: GetDepartureDatesFromOffset = (beginDate, format) => R.pipe(
  R.defaultTo([]),
  R.ifElse(
    R.pipe(R.head, R.isNil),
    R.always([]),
    R.map(
      offset => moment(beginDate).add(parseInt(offset, 10), 'days').format(format)
    )
  )
)

export const isValidList: IsValidList = list => (
  Array.isArray(list) && list.length > 0
)

export const getDateByOffsetAndSaleBeginDate: GetDateByOffsetAndSaleBeginDate = (dateOffset, dateOrigin) => (
  moment(dateOrigin).add(dateOffset, 'days')
)

export const getFormattedCurrentDay: GetFormattedCurrentDay = () => (
  moment().format('YYYYMMDD')
)

export const getRefreshedFilters: GetRefreshedFilters = (data, filters) => {
  if (Array.isArray(data)) {
    return data
  } else {
    const selectedFilters = [...filters] // trick to create new ref
    const valueIndex = R.findIndex(R.equals(data), selectedFilters)

    if (valueIndex === -1) {
      selectedFilters.push(data)
    } else {
      selectedFilters.splice(valueIndex, 1)
    }
    return selectedFilters
  }
}

export const getTransportationFromDepartureCities: GetTransportationFromDepartureCities = (departuresCities) => {
  if (departuresCities.some(city => !DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(city))) {
    return 'included'
  } else if (departuresCities.some(city => DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(city))) {
    return 'none'
  } else {
    return 'all'
  }
}

export const getDepartureCitiesFromTransportation: GetDepartureCitiesFromTransportation = (departureCities, selectedDeparturesCities, transportation) => {
  if (selectedDeparturesCities?.length) return selectedDeparturesCities

  switch (transportation) {
    case 'included':
      return departureCities.filter(city => !DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(city.key)).map(city => city.key)
    case 'none':
      return departureCities.filter(city => DEPARTURE_CITIES_NO_TRANSPORTATION_CODES.includes(city.key)).map(city => city.key)
    case 'all':
    default:
      return []
  }
}
